import { useState } from 'react';
import { usePageVisit } from '../../hooks/useAnalytics';

export default function LieDetectorPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Track lie detector page visit
  usePageVisit('lie-detector');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);

      const response = await fetch('https://readdy.ai/api/form/d4si5b15bv6k73lc3j7g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="bg-[#1A1A1A] text-white min-h-screen" style={{fontFamily: 'Montserrat, sans-serif'}}>
      {/* STATUS BAR - Terminal Green */}
      <div className="bg-[#0D0D0D] border-b-2 border-[#00FF00] py-3 md:py-4 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex items-center gap-3 md:gap-4">
          <div className="w-3 h-3 md:w-4 md:h-4 bg-[#00FF00] rounded-full animate-pulse"></div>
          <p className="text-[11px] sm:text-[13px] md:text-[15px] font-mono tracking-wider text-[#00FF00] font-bold">
            STATUS: SECURE // DOWNLOAD SENT TO INBOX
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        {/* Dark Concrete Background */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("https://public.readdy.ai/ai/img_res/74a86e5c6a3274fb89a55793533ab3b0.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
          <div className="absolute inset-0 bg-black/85"></div>
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-6 text-center">
          <h1 className="text-[36px] sm:text-[48px] md:text-[64px] lg:text-[80px] font-black text-[#D4AF37] mb-3 md:mb-4 tracking-tight leading-[1.1]" style={{fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em'}}>
            THE GOLD DEALER<br/>LIE DETECTOR
          </h1>
          <p className="text-[14px] sm:text-[16px] md:text-[20px] font-mono text-[#00FF00] tracking-wider mb-4 md:mb-6">
            INTERROGATION PROTOCOL: ACTIVE
          </p>
          <p className="text-[14px] sm:text-[16px] md:text-[18px] text-white/90 max-w-[900px] mx-auto leading-[1.7]">
            The 10-Point Interrogation Sheet to Stop Scams & Save Your Money.
          </p>
        </div>
      </section>

      {/* Instructions Banner */}
      <section className="relative py-8 md:py-12">
        <div className="absolute inset-0 bg-[#FF0000]/10"></div>
        <div className="relative z-10 max-w-[1000px] mx-auto px-4 md:px-6">
          <div className="bg-[#FF0000]/20 border-2 border-[#FF0000] rounded-lg p-4 md:p-6">
            <h2 className="text-[18px] md:text-[22px] font-black text-[#FF0000] mb-3 text-center" style={{fontFamily: 'Oswald, sans-serif'}}>
              ⚠️ INSTRUCTIONS ⚠️
            </h2>
            <p className="text-[14px] md:text-[16px] text-white leading-[1.7] text-center">
              Do not buy a single ounce of gold until you ask these 10 questions. If a dealer gets annoyed, evasive, or gives you a "Bad Answer," <strong>HANG UP</strong>. They are trying to rip you off.
            </p>
          </div>
        </div>
      </section>

      {/* The Digital Dossier */}
      <section className="relative py-12 md:py-20">
        {/* Dark Concrete Background */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("https://readdy.ai/api/search-image?query=Dark%20charcoal%20concrete%20wall%20texture%20with%20rough%20surface%2C%20industrial%20warehouse%20aesthetic%2C%20high%20contrast%20shadows%2C%20gritty%20urban%20military%20style%2C%20pure%20black%20and%20dark%20gray%20tones%2C%20tactical%20operations%20room%20background%2C%20ultra%20high%20resolution%20seamless%20pattern&width=1920&height=1080&seq=dark-concrete-bg2&orientation=landscape")',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
          <div className="absolute inset-0 bg-black/90"></div>
        </div>

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 md:px-6">
          {/* Case File Container */}
          <div className="relative bg-[#F5F1E8] rounded-lg shadow-2xl p-6 md:p-12 border-8 border-[#8B7355]" style={{
            backgroundImage: 'linear-gradient(to bottom, #F5F1E8 0%, #E8E4DC 100%)'
          }}>
            {/* CONFIDENTIAL Watermark */}
            <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
              <p className="text-[80px] md:text-[150px] font-black rotate-[-25deg] text-[#FF0000]" style={{fontFamily: 'Oswald, sans-serif'}}>
                CONFIDENTIAL
              </p>
            </div>

            {/* Red Stamp */}
            <div className="absolute top-4 right-4 md:top-8 md:right-8">
              <div className="border-4 border-[#FF0000] rounded-full w-20 h-20 md:w-28 md:h-28 flex items-center justify-center rotate-12 opacity-60">
                <div className="text-center">
                  <p className="text-[10px] md:text-[13px] font-black text-[#FF0000]" style={{fontFamily: 'Oswald, sans-serif'}}>CLASSIFIED</p>
                  <p className="text-[8px] md:text-[10px] font-bold text-[#FF0000]">2025</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="relative space-y-8 md:space-y-10">
              {/* Section Header: THE PRICE TRAPS */}
              <div className="border-b-4 border-[#FF0000] pb-4">
                <h2 className="text-[24px] md:text-[32px] font-black text-[#FF0000] text-center" style={{fontFamily: 'Oswald, sans-serif'}}>
                  🛑 THE PRICE TRAPS
                </h2>
              </div>

              {/* Question 1 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  1. "What is the 'Spot Price' you are using right now?"
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> The "Spot Price" is the live stock market price of gold. Some dealers lie and say the price is higher than it really is.
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> They give you a number very close to what you see on TV or Google (e.g., "$2,650").
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "Don't worry about the spot price, we focus on value." (RUN. They are hiding the price).
                    </p>
                  </div>
                </div>
              </div>

              {/* Question 2 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  2. "How much EXTRA am I paying over the Spot Price?" (The Premium)
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> Dealers have to make a profit, but some charge huge fees (premiums). You want "Bullion" (cheap fees), not "Collectibles" (huge fees).
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> "You are paying 3% to 5% over spot." (This is fair).
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "The price is just $3,000 flat." (They won't tell you the percentage because they are charging you 30% extra).
                    </p>
                  </div>
                </div>
              </div>

              {/* Question 3 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  3. "If I sell this EXACT coin back to you in 5 minutes, how much will you pay me?"
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> This shows you how much money you lose the second you buy. This is called the "Spread."
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> "We will pay you the Spot Price." (You only lose the small fee you paid in Q2).
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "We buy back at market rates." (Vague means they will lowball you).
                    </p>
                  </div>
                </div>
              </div>

              {/* Section Header: THE "MAGIC GOLD" TRAPS */}
              <div className="border-b-4 border-[#FF0000] pb-4 pt-6">
                <h2 className="text-[24px] md:text-[32px] font-black text-[#FF0000] text-center" style={{fontFamily: 'Oswald, sans-serif'}}>
                  🛑 THE "MAGIC GOLD" TRAPS
                </h2>
              </div>

              {/* Question 4 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  4. "Is this a 'Collectible' coin or 'Bullion'?"
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> "Bullion" is just gold weight. "Collectible" (or Numismatic) is a fancy story used to charge you double. You are an investor, not a coin collector.
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> "This is standard Bullion. It's for investing."
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "This is a rare semi-numismatic coin with historical value." (SCAM ALERT: They are selling you a story, not gold).
                    </p>
                  </div>
                </div>
              </div>

              {/* Question 5 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  5. "Can the Government confiscate this specific coin?"
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> Scammers scare you by saying "The government will take regular gold, but not THIS special coin." It is a lie to make you buy the expensive "special" coin.
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> "Gold is gold. There is no magic coin that the government can't touch."
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "Yes! You need this special 'Proof' coin to be safe from seizure." (LIE. Hang up immediately).
                    </p>
                  </div>
                </div>
              </div>

              {/* Section Header: THE "GHOST" TRAPS */}
              <div className="border-b-4 border-[#FF0000] pb-4 pt-6">
                <h2 className="text-[24px] md:text-[32px] font-black text-[#FF0000] text-center" style={{fontFamily: 'Oswald, sans-serif'}}>
                  🛑 THE "GHOST" TRAPS
                </h2>
              </div>

              {/* Question 6 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  6. "Do you have this in your vault RIGHT NOW, or do you have to order it?"
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> Some dealers sell gold they don't have. They use your money to go buy it. If they go broke before they buy it, you lose everything.
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> "It is in stock and ready to ship."
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "We have a network of suppliers..." (Means they don't have it).
                    </p>
                  </div>
                </div>
              </div>

              {/* Question 7 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  7. "How many days until I get a tracking number?"
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> If it takes more than 5 days, they are "floating" your money.
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> "Ships within 3 to 5 business days."
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "Due to high demand, it may take 4 to 8 weeks." (You are giving them an interest-free loan).
                    </p>
                  </div>
                </div>
              </div>

              {/* Section Header: THE TRUST TRAPS */}
              <div className="border-b-4 border-[#FF0000] pb-4 pt-6">
                <h2 className="text-[24px] md:text-[32px] font-black text-[#FF0000] text-center" style={{fontFamily: 'Oswald, sans-serif'}}>
                  🛑 THE TRUST TRAPS
                </h2>
              </div>

              {/* Question 8 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  8. "How do you test for Tungsten fakes?"
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> Fake gold bars are filled with Tungsten (a cheap metal). Simple X-Ray machines can't see inside.
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> "We use Ultrasonic testing or a Sigma Verifier to see all the way through the bar."
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "We buy from trusted mints, so we don't need to test." (Lazy and dangerous).
                    </p>
                  </div>
                </div>
              </div>

              {/* Question 9 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  9. "Are you on commission?"
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> A "Financial Advisor" is paid to help you. A "Broker" is paid to sell you the most expensive thing. Know who you are talking to.
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> "Yes, I earn a commission." (Honesty is good).
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "I am a Senior Strategist." (They are using a fancy title to hide that they are a salesperson).
                    </p>
                  </div>
                </div>
              </div>

              {/* Question 10 */}
              <div className="border-l-4 border-[#1A1A1A] pl-4 md:pl-6">
                <h3 className="text-[16px] md:text-[20px] font-bold text-[#1A1A1A] mb-3 font-mono">
                  10. "Will you put that promise in an email right now?"
                </h3>
                <p className="text-[13px] md:text-[15px] text-[#2C3E50] mb-3 leading-[1.6] italic">
                  <strong>Why ask:</strong> If they promise buy-backs or guarantees on the phone but won't email it, they are lying.
                </p>
                <div className="space-y-2">
                  <div className="bg-green-50 border-l-4 border-green-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-green-800 font-semibold leading-[1.6]">
                      ✅ <strong>Good Answer:</strong> "Sure, what's your email address?"
                    </p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-600 p-3 md:p-4">
                    <p className="text-[13px] md:text-[15px] text-red-800 font-semibold leading-[1.6]">
                      ❌ <strong>Bad Answer:</strong> "Our calls are recorded, so you don't need an email." (RUN).
                    </p>
                  </div>
                </div>
              </div>

              {/* The Golden Rule */}
              <div className="border-4 border-[#D4AF37] bg-[#D4AF37]/10 rounded-lg p-6 md:p-8 mt-8">
                <h2 className="text-[20px] md:text-[26px] font-black text-[#D4AF37] text-center mb-4" style={{fontFamily: 'Oswald, sans-serif'}}>
                  ⭐ THE GOLDEN RULE ⭐
                </h2>
                <p className="text-[15px] md:text-[18px] text-[#1A1A1A] text-center leading-[1.7] font-semibold">
                  If it feels confusing, it's because they are trying to confuse you.<br/>
                  Real gold is simple. It's weight times price. Anything else is a trap.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recommendation Section */}
      <section className="relative py-16 md:py-24 bg-[#0A1628]">
        <div className="max-w-[900px] mx-auto px-4 md:px-6 text-center">
          <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-black text-white mb-6 md:mb-8 leading-[1.2]" style={{fontFamily: 'Oswald, sans-serif'}}>
            I Vetted 12 Dealers.<br/>
            <span className="text-[#D4AF37]">Only One Passed.</span>
          </h2>
          
          <div className="space-y-4 md:space-y-6 mb-8 md:mb-10">
            <p className="text-[14px] md:text-[16px] text-white/80 leading-[1.8]">
              I didn't just write this list for you; I wrote it for myself. When I called around to secure my own portfolio, most dealers failed Question 2 (Premiums) or Question 5 (Confiscation Fear).
            </p>
            <p className="text-[14px] md:text-[16px] text-white/80 leading-[1.8]">
              One company answered all 10 questions correctly, transparently, and without the high-pressure sales tactics.
            </p>
            <p className="text-[14px] md:text-[16px] text-white/90 leading-[1.8] font-semibold">
              <span className="text-[#D4AF37]">Noble Gold Investments</span> is who I trust. They focus on education, safe storage, and fair pricing. If you want to skip the interrogation and go straight to the source I use, start here.
            </p>
          </div>

          <a 
            href="https://www.noblegoldinvestments.com/Epic"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:from-[#FFD700] hover:to-[#D4AF37] text-[#1A1A1A] px-8 md:px-12 py-4 md:py-5 rounded-lg text-[14px] md:text-[18px] font-bold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-2xl whitespace-nowrap cursor-pointer"
          >
            GET THE FREE WEALTH PROTECTION GUIDE
          </a>

          <p className="text-[12px] md:text-[14px] text-white/60 mt-4 md:mt-6">
            Minimum investment applies. Perfect for IRA Rollovers and Physical Delivery.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] border-t border-[#D4AF37]/20 py-8 md:py-12">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0">
            <div className="flex items-center gap-3 md:gap-4">
              <img 
                src="https://static.readdy.ai/image/93c3e079cd1f297eadf6098c3b0ef55d/031439dfb7c5b3457d1186c629c535e0.png" 
                alt="Shield Logo"
                className="h-8 w-8 md:h-10 md:w-10 object-contain"
              />
              <p className="text-xs text-white/70">Epic Real Estate</p>
            </div>

            <div className="flex items-center gap-4 md:gap-8">
              <a href="/" className="text-xs md:text-sm text-white/70 hover:text-[#D4AF37] transition-colors whitespace-nowrap">
                Back to Main Site
              </a>
              <a href="/privacy-policy" className="text-xs md:text-sm text-white/70 hover:text-[#D4AF37] transition-colors whitespace-nowrap">
                Privacy Policy
              </a>
              <a href="/terms-and-conditions" className="text-xs md:text-sm text-white/70 hover:text-[#D4AF37] transition-colors whitespace-nowrap">
                Terms &amp; Conditions
              </a>
            </div>
          </div>

          <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-white/10 text-center">
            <p className="text-[10px] md:text-xs text-white/50">
              © 2026 Epic Real Estate. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
